/**
 * Task #5799 — transient-aware retry for idempotent client reads.
 *
 * The highest-volume production Sentry errors on the car-detail / booking
 * widget surfaces were `ApiError: API error 500` and `QuoteApiError: API
 * error 500` (DRIVESHARE_V2-2P / -8Q). Their breadcrumbs showed many
 * *different* GET endpoints (availability, reviews, quote, …) failing
 * together in a single session, and re-hitting each one live returned
 * `200` — i.e. momentary backend blips (pooler/transport saturation), not
 * a persistent per-endpoint bug.
 *
 * `fetchWithTransientRetry` smooths those blips over by retrying a small,
 * bounded number of times with a short capped backoff — but ONLY for
 * idempotent requests (`GET` / `HEAD`). Any request carrying a mutating
 * method (`POST` / `PUT` / `PATCH` / `DELETE` — the paths that create
 * bookings, charges, or holds) is issued exactly once and never retried,
 * so a retry can never double-submit a write.
 *
 * A retry is triggered by either:
 *  - a transient server error (HTTP status `>= 500`), or
 *  - a network-level failure (`fetch` rejecting, e.g. a dropped
 *    connection / DNS blip).
 *
 * 4xx responses (including the existing `429` rate-limit handling that
 * each caller already branches on) are returned unchanged and are NEVER
 * retried here. Once the bounded retries are exhausted the last response
 * (or the last network error) is surfaced exactly as before, so a
 * genuine, persistent failure still throws and remains visible in
 * logs / Sentry — nothing is silently swallowed.
 */

/** Status at or above which a response is treated as a transient server error. */
const RETRYABLE_STATUS_MIN = 500;

/**
 * Keep a short-lived, in-memory view of fetches that rejected before an HTTP
 * response existed. This is intentionally process-local: it is diagnostic
 * context for the current browser session, not a durable event stream.
 */
export const RECENT_NETWORK_FAILURE_WINDOW_MS = 30_000;

type NetworkFailure = {
  requestId: number;
  route: string;
  occurredAt: number;
};

const recentNetworkFailures: NetworkFailure[] = [];
let nextRequestId = 0;

function normalizeRoute(input: string): string {
  try {
    const url = new URL(
      input,
      typeof window !== "undefined" && window.location?.origin
        ? window.location.origin
        : "http://localhost",
    );
    // API callers pass `/api/...`, while booking telemetry uses `/bookings/...`.
    // Put both shapes into the same namespace before comparing routes.
    return url.pathname.replace(/^\/api(?=\/|$)/, "") || "/";
  } catch {
    return input.split("?")[0].replace(/^\/api(?=\/|$)/, "") || "/";
  }
}

function pruneRecentNetworkFailures(now: number): void {
  const cutoff = now - RECENT_NETWORK_FAILURE_WINDOW_MS;
  while (recentNetworkFailures.length > 0 && recentNetworkFailures[0].occurredAt < cutoff) {
    recentNetworkFailures.shift();
  }
}

/**
 * Record one logical request's network-level failure. Retries of the same
 * logical request are deliberately represented once so retry policy cannot
 * make a browser outage look larger than it is.
 */
function recordNetworkFailure(input: string, requestId: number): void {
  const occurredAt = Date.now();
  pruneRecentNetworkFailures(occurredAt);
  if (recentNetworkFailures.some((failure) => failure.requestId === requestId)) return;
  recentNetworkFailures.push({
    requestId,
    route: normalizeRoute(input),
    occurredAt,
  });
}

export interface RecentNetworkFailureSummary {
  /** Number of distinct logical requests, excluding the timed-out route. */
  unrelatedRequestCount: number;
  /** Distinct normalized routes for those requests. */
  unrelatedRoutes: string[];
}

/**
 * Return recent status-less failures from other routes. Two or more distinct
 * routes are enough to identify a client-wide connectivity cluster; a single
 * route may simply be a route-specific transport failure.
 */
export function getRecentNetworkFailureSummary(route: string): RecentNetworkFailureSummary {
  const now = Date.now();
  pruneRecentNetworkFailures(now);
  const normalizedRoute = normalizeRoute(route);
  const unrelated = recentNetworkFailures.filter(
    (failure) => failure.route !== normalizedRoute,
  );
  const unrelatedRoutes = [...new Set(unrelated.map((failure) => failure.route))];
  return {
    unrelatedRequestCount: unrelated.length,
    unrelatedRoutes,
  };
}

/** Test-only reset hook; production code never needs to clear this context. */
export function clearRecentNetworkFailures(): void {
  recentNetworkFailures.length = 0;
}

/**
 * Task #8077 — the retry schedule is sized to ride out the deploy
 * switchover window. During a publish the old VM is torn down before the
 * new one binds the port, so the edge proxy answers every `/api/*`
 * request with a bare 500 for ~15 seconds (DRIVESHARE_V2-8F: dashboard
 * bootstrap reads 500ing at the exact second of a "Published your App"
 * commit). The previous schedule (2 retries, ~0.9s total) gave up long
 * before the window closed, so every logged-in user with a dashboard
 * open during a publish saw a broken page.
 *
 * The schedule is deadline-driven rather than purely count-driven:
 *  - Any failure observed before RECOVERY_WINDOW_MS has elapsed earns
 *    another retry (so a failure at 14.9s still schedules one more
 *    attempt AFTER the window — the schedule cannot give up moments
 *    before the edge recovers).
 *  - Attempt STARTS are deadline-bounded: each backoff sleep is capped
 *    to the remaining budget, and a wake-up is re-checked against the
 *    deadline plus a small grace (LATE_WAKE_GRACE_MS) that absorbs
 *    normal timer jitter — so a sleep capped exactly to the deadline
 *    still starts its (guaranteed) final attempt, while a badly late
 *    timer (throttled background tab) gives up instead of starting an
 *    attempt far outside the window. No attempt ever starts after
 *    TOTAL_RETRY_BUDGET_MS + LATE_WAKE_GRACE_MS. An attempt already in
 *    flight at the deadline runs to natural completion (it is not
 *    aborted — a late success is still a success for the caller).
 *  - MAX_TRANSIENT_RETRIES is a belt-and-braces attempt cap in case
 *    time appears frozen (e.g. mocked timers in tests).
 *
 * A genuinely-down backend still surfaces an error — just after the
 * budget instead of ~1s. That trade is deliberate: publishes happen
 * daily; a hard backend outage is rare, and the error still reaches
 * logs/Sentry unchanged once retries are exhausted.
 */

/**
 * A failure observed before this much elapsed time always earns one more
 * retry, guaranteeing at least one attempt lands after the ~15s deploy
 * switchover window has closed.
 */
const RECOVERY_WINDOW_MS = 16_000;
/**
 * Absolute cap on additional attempts after the first. With instant
 * failures the doubling schedule below places attempts at ~0.3s, 0.9s,
 * 2.1s, 4.5s, 9.3s, 14.3s, 19.3s — 7 retries; 8 leaves one spare.
 */
const MAX_TRANSIENT_RETRIES = 8;

/** First backoff delay; doubles each attempt, capped at MAX_BACKOFF_MS. */
const BASE_BACKOFF_MS = 300;
const MAX_BACKOFF_MS = 5_000;
/**
 * Ceiling on when a retry attempt may be STARTED (first attempt
 * included). Sleeps are capped so the next attempt begins at or before
 * this deadline (plus the jitter grace below).
 */
const TOTAL_RETRY_BUDGET_MS = 20_000;

/**
 * Timer-jitter grace for the post-sleep deadline re-check. A sleep capped
 * to the remaining budget wakes at ~the deadline, and real timers are
 * routinely delivered slightly late (loaded main thread) — without a
 * grace, the guaranteed post-window final attempt would be lost to
 * millisecond lateness at exactly the moment it matters. Only wake-ups
 * later than deadline+grace (e.g. minutes-late throttled background-tab
 * timers) give up instead of fetching.
 */
const LATE_WAKE_GRACE_MS = 1_500;

// Note: Task #8075 previously fingerprinted the Replit edge's bare
// `Internal Server Error` 500 (plain-text, non-JSON) to grant ONLY that
// shape an extended retry budget. Task #8077's deadline-driven schedule
// above applies the full switchover budget to ALL transient 5xx on
// idempotent reads — a deliberate superset (see the trade-off note on
// MAX_TRANSIENT_RETRIES), so the edge fingerprint check was removed.

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Only idempotent reads are safe to retry. Treat a missing method as
 * `GET` (the `fetch` default), and uppercase so `"get"` / `"Get"` match.
 */
function isIdempotentMethod(init?: RequestInit): boolean {
  const method = (init?.method ?? "GET").toUpperCase();
  return method === "GET" || method === "HEAD";
}

function backoffMs(attempt: number, capMs: number = MAX_BACKOFF_MS): number {
  return Math.min(capMs, BASE_BACKOFF_MS * 2 ** attempt);
}

/**
 * A `fetch` drop-in that transparently retries transient failures for
 * idempotent (`GET` / `HEAD`) requests only. See the module docstring for
 * the full contract.
 *
 * The return value is the raw `Response`; the caller keeps full ownership
 * of body parsing and error mapping (so the existing `ApiError` /
 * `QuoteApiError` / `RateLimitError` shapes are preserved unchanged).
 */
export async function fetchWithTransientRetry(
  input: string,
  init?: RequestInit,
): Promise<Response> {
  const canRetry = isIdempotentMethod(init);
  const startedAt = Date.now();
  const requestId = ++nextRequestId;
  let recordedNetworkFailure = false;
  let attempt = 0;
  /**
   * Decide whether a just-observed failure earns a retry, and if so sleep
   * until the next attempt may start. Retries continue for any failure
   * observed inside the recovery window; the sleep is capped so the next
   * attempt never starts after the total budget deadline.
   */
  const retryAfterBackoff = async (): Promise<boolean> => {
    if (!canRetry || attempt >= MAX_TRANSIENT_RETRIES) return false;
    const elapsed = Date.now() - startedAt;
    // Only failures seen inside the recovery window earn a retry; a
    // failure at/after the window means the outage outlived the deploy
    // switchover this schedule exists to cover.
    if (elapsed >= RECOVERY_WINDOW_MS) return false;
    const remaining = TOTAL_RETRY_BUDGET_MS - elapsed;
    if (remaining <= 0) return false;
    await sleep(Math.min(backoffMs(attempt), remaining));
    // Re-check after waking: browser timers fire late in throttled /
    // background tabs, so the sleep alone cannot guarantee the deadline.
    // A wake at or slightly past the deadline (within the jitter grace)
    // still starts its attempt — this is what preserves the guaranteed
    // post-window final attempt when the sleep was capped to the exact
    // deadline. Only a badly late wake surfaces the already-observed
    // failure instead of starting an attempt far outside the window.
    if (Date.now() - startedAt > TOTAL_RETRY_BUDGET_MS + LATE_WAKE_GRACE_MS) return false;
    attempt += 1;
    return true;
  };
  for (;;) {
    try {
      const res = await fetch(input, init);
      if (res.status >= RETRYABLE_STATUS_MIN && (await retryAfterBackoff())) {
        continue;
      }
      return res;
    } catch (err) {
      // Task #8483 — an aborted request (caller-supplied `AbortSignal`,
      // e.g. a bounded stall timeout or a component-unmount cleanup) means
      // the caller no longer wants this request. Retrying would either
      // pointlessly spin against an already-aborted signal (every attempt
      // rejects instantly) or reissue a network call the caller already
      // gave up on. Re-throw immediately so the caller's own timeout /
      // cleanup handling decides what happens next.
      if ((err as { name?: string } | null)?.name === "AbortError") throw err;
      // Network-level failure (fetch rejected). Same deadline-driven
      // retry policy for idempotent reads; otherwise re-throw so the
      // caller sees the original error exactly as today.
      if (!recordedNetworkFailure) {
        recordedNetworkFailure = true;
        recordNetworkFailure(input, requestId);
      }
      if (await retryAfterBackoff()) continue;
      throw err;
    }
  }
}
